import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "configureStore";
import { Card, TextField, Select, Badge as PolarisBadge, Button as PolarisButton, Banner } from "@shopify/polaris";
import useMediaQuery from "containers/work-security/use-media-query";
import DataTable from "components/datatable/Datatable";
import DataTableSkeleton from "components/datatable/DataTableSkeleton";
import { Column } from "types/Datatable";
import { PHONE_PAGE_GUTTER, PHONE_PAGE_MARGIN } from "components/tw/pageGutter";
import {
  setModalxComponent,
  setModalxComponentProps,
  setModalxOptions,
} from "components/modalx/modalx.slice";
import {
  fetchWorkSecurityAssignments,
  UserAssignment,
  AssignableRole,
} from "services/work-security.service";
import { WorkSecurityArea, WorkSecurityAreaScopeProps } from "services/work-security-areas.service";

interface Props extends WorkSecurityAreaScopeProps {
  /** Accessible areas (from the module header's useAreaScope) — used to label role badges. */
  areas?: WorkSecurityArea[];
}

/** Falls back to a generic label for a role whose area was since archived (excluded from `areas`). */
function areaLabel(areas: WorkSecurityArea[], t: any, areaId?: string | null): string {
  const found = areaId ? areas.find((a) => a.id === areaId) : undefined;
  return found ? found.name : t("area_unknown", { defaultValue: "Other sphere" });
}

/**
 * Reusable body for the Work Security role-assignments dashboard.
 * Renders its own "Assign Roles to User" action so it can be embedded both as a
 * standalone page and as a tab inside the Work Security page (no <Page> wrapper).
 *
 * NOTE (PR-2 §P3, verified against api/controllers/work-security/fetch-assignments.js):
 * this endpoint has no server-side `areaId` filter — it always returns roles
 * and assignments across every area the caller can access, tagging each role
 * with `area` for the UI to label/filter with (backend commit: "the UI needs
 * to label each role with the one it lives in"). Narrowing to the selected
 * sphere and labelling roles in "all areas" mode both happen client-side here.
 */
export default function WorkSecurityAssignmentsPanel({
  areaId,
  selectedArea,
  areas = [],
}: Props = {}) {
  const { companyId } = useParams<{ companyId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const { t } = useTranslation();
  // "All areas" mode, and only worth labelling when there is more than one
  // sphere to disambiguate — matches the module header's own switcher gate.
  const showAreaLabel = !areaId && areas.length > 1;
  // A touch screen scrolls the page, not a table boxed in the page, and 100vh includes the browser bar there.
  const isTouchScreen = useMediaQuery("(pointer: coarse)");

  const [users, setUsers] = useState<UserAssignment[]>([]);
  const [availableRoles, setAvailableRoles] = useState<AssignableRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Filters State
  const [textSearch, setTextSearch] = useState("");
  const [debouncedTextSearch, setDebouncedTextSearch] = useState("");
  const [selectedRoleFilter, setSelectedRoleFilter] = useState("");
  const assignmentsRequestRef = useRef(0);

  // A concrete sphere selection narrows the (unfiltered-by-the-backend) roles
  // list to just that sphere's roles — see the module-level NOTE above.
  const scopedRoles = areaId
    ? availableRoles.filter((r) => r.area === areaId)
    : availableRoles;

  // Switching spheres drops a role filter that no longer applies, the same
  // way the roles tab resets its own filters (PR-2 §P1).
  useEffect(() => {
    const scopedRoleIds = new Set(scopedRoles.map((r) => r.id));
    setSelectedRoleFilter((current) =>
      current && !scopedRoleIds.has(current) ? "" : current
    );
    // Only the sphere switch itself should trigger this — re-checking on
    // every `availableRoles` refresh would also fire right after the roles
    // list this effect depends on gets (re)loaded, which is harmless but
    // unnecessary.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaId]);

  const loadAssignments = async () => {
    const requestId = ++assignmentsRequestRef.current;
    if (!companyId) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await fetchWorkSecurityAssignments(companyId, {
        textSearch: debouncedTextSearch,
        role: selectedRoleFilter,
      });
      if (requestId !== assignmentsRequestRef.current) return;
      setLoadError(false);
      setUsers(data.users);
      setAvailableRoles(data.roles);
    } catch (err) {
      if (requestId !== assignmentsRequestRef.current) return;
      console.error("Failed to load work security assignments", err);
      setLoadError(true);
    } finally {
      if (requestId === assignmentsRequestRef.current) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadAssignments();
  }, [companyId, debouncedTextSearch, selectedRoleFilter]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedTextSearch(textSearch);
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [textSearch]);

  // Open Assignments Modal for a single user (Manage Roles)
  const handleManageRolesClick = (userId: string, userName: string, currentRoles: { id: string; name: string }[]) => {
    dispatch(
      setModalxOptions({
        title: t("assign_user_roles", { defaultValue: "Assign Roles" })
      })
    );
    dispatch(setModalxComponent("WorkSecurityUserRolesModal"));
    dispatch(
      setModalxComponentProps({
        userId,
        userName,
        initialRoleIds: currentRoles.map((r) => r.id),
        availableRoles,
        onSuccess: () => {
          loadAssignments();
        },
      })
    );
  };

  // Open Assignments Modal for a new user select (Assign Roles to User)
  const handleAssignRolesToUserClick = () => {
    dispatch(
      setModalxOptions({
        title: t("assign_roles_to_user", { defaultValue: "Assign Roles to User" })
      })
    );
    dispatch(setModalxComponent("WorkSecurityUserRolesModal"));
    dispatch(
      setModalxComponentProps({
        availableRoles,
        onSuccess: () => {
          loadAssignments();
        },
      })
    );
  };

  const tableColumns: Column[] = [
    {
      key: "name",
      name: t("Name", { defaultValue: "Name" }),
      type: "custom",
      mobileView: "topLeft",
      render: (item: UserAssignment) => (
        <div>
          <p className="font-semibold text-zinc-900 dark:text-white">{item.name}</p>
          <p className="text-xs text-zinc-500">{item.email}</p>
        </div>
      ),
    },
    {
      key: "position",
      name: t("position", { defaultValue: "Position" }),
      type: "custom",
      mobileView: "topRight",
      render: (item: UserAssignment) => (
        <span className="text-sm text-zinc-700 dark:text-zinc-300">
          {item.position || "-"}
        </span>
      ),
    },
    {
      key: "assignedRoles",
      name: t("assigned_roles", { defaultValue: "Assigned Roles" }),
      type: "custom",
      mobileView: "bottomLeft",
      render: (item: UserAssignment) => {
        // A concrete sphere selection shows only that sphere's roles here too
        // — see the module-level NOTE above (no server-side filter exists).
        const roleAreaById = new Map(availableRoles.map((r) => [r.id, r.area]));
        const visibleRoles = areaId
          ? item.assignedRoles.filter((role) => roleAreaById.get(role.id) === areaId)
          : item.assignedRoles;
        return (
          <div className="flex flex-wrap gap-1">
            {visibleRoles.length === 0 ? (
              <span className="text-xs text-zinc-400 italic">
                {t("no_roles_assigned", { defaultValue: "No roles assigned" })}
              </span>
            ) : (
              visibleRoles.map((role) => (
                <div key={role.id} className="flex flex-col items-start gap-0.5">
                  <PolarisBadge tone="info">{role.name}</PolarisBadge>
                  {showAreaLabel && (
                    <span className="text-[10px] leading-none text-zinc-400">
                      {areaLabel(areas, t, roleAreaById.get(role.id))}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        );
      },
    },
    {
      key: "actions",
      name: t("Action", { defaultValue: "Action" }),
      type: "custom",
      mobileView: "bottomRight",
      render: (item: UserAssignment) => (
        <button
          type="button"
          onClick={() => handleManageRolesClick(item.id, item.name, item.assignedRoles)}
          className="inline-flex min-h-11 items-center justify-center rounded-lg border border-zinc-300 bg-white px-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50 md:min-h-10"
        >
          {t("manage_roles", { defaultValue: "Manage Roles" })}
        </button>
      ),
    },
  ];

  return (
    <div className="space-y-4 mt-4">
      <div className={`flex justify-end ${PHONE_PAGE_GUTTER}`}>
        <PolarisButton
          variant="primary"
          disabled={loading}
          onClick={handleAssignRolesToUserClick}
        >
          {t("assign_roles_to_user", { defaultValue: "Assign Roles to User" })}
        </PolarisButton>
      </div>

      {loadError && (
        <Banner tone="critical" onDismiss={() => setLoadError(false)}>
          {t("assignments_load_failed", { defaultValue: "Unable to load role assignments. Please try again." })}
        </Banner>
      )}

      {/* Filters Card */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-2 items-end">
          <div className="md:col-span-2">
            <TextField
              label={t("search_users", { defaultValue: "Search Users" })}
              value={textSearch}
              onChange={setTextSearch}
              placeholder={t("search_by_name_or_email", { defaultValue: "Search by name or email..." })}
              autoComplete="off"
              clearButton
              onClearButtonClick={() => setTextSearch("")}
            />
          </div>
          <div>
            <Select
              label={t("filter_by_role", { defaultValue: "Filter by Role" })}
              options={[
                { label: t("all_roles", { defaultValue: "All Roles" }), value: "" },
                ...scopedRoles.map((r) => ({ label: r.name, value: r.id })),
              ]}
              value={selectedRoleFilter}
              onChange={setSelectedRoleFilter}
            />
          </div>
        </div>
      </Card>

      {/* Data Table */}
      <div className={`bg-white rounded-xl shadow-sm border border-zinc-200 overflow-hidden dark:bg-zinc-900 dark:border-zinc-800 ${PHONE_PAGE_MARGIN}`}>
        {loading ? (
          <DataTableSkeleton rows={10} columns={tableColumns.length} />
        ) : (
          <DataTable
            collection={users}
            columns={tableColumns}
            maxBodyHeight={isTouchScreen ? undefined : "calc(100vh - 300px)"}
          />
        )}
      </div>
    </div>
  );
}
